import * as React from 'react';
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {SUB_DIR, URL, CONTENT_TYPE, FORM_ACTION_TYPE, FLOW_STATUS, ORDER_BY, RECORDS_SORT_BY} from '../../Config/appConfig';
import {CoverNote, RecordCount, InsurerId, SortBy, ReduxUpdatedState} from '../../Config/dataModel';
import coverNoteDispatch from '../../ReduxRequests/Dispatch/coverNoteDispatch';
import policyDispatch from '../../ReduxRequests/Dispatch/policyDispatch';
import authState from '../../ReduxRequests/State/authState';
import coverNoteState from '../../ReduxRequests/State/coverNoteState';
import policyState from '../../ReduxRequests/State/policyState';
import ErrorHandler from '../ErrorHandler/ErrorHandler';
import NoRecords from './NoRecords';
import RecordsBannerButton from './RecordsBannerButton';
import RecordsSquareButton from './RecordsSquareButton';
import RecordsTableView from './RecordsTableView';
import MaintenanceNotification from '../Maintenance/MaintenanceNotification';
import util from '../../Util/util';
import * as moment from 'moment';
import '../../style/records.css';

const pageSpinner = require('../../resource/img/page-loading.gif');

const mapDispatchToProps = (dispatch) => {
    return {
        ...coverNoteDispatch(dispatch),
        ...policyDispatch(dispatch)
    }
};

const mapStateToProps = (state) => {
    return {
        ...coverNoteState(state),
        ...policyState(state),
        ...authState(state)
    }
};

interface RecordsProps {
    content: string,
    newRecord: CoverNote,
    newCount?: RecordCount,
    insurerId: () => InsurerId,
    fetchAllCoverNotes: (sortBy: SortBy) => void,
    allCoverNotes: CoverNote[],
    fetchCoverNoteCount: (insurerId: InsurerId) => void,
    coverNoteCount: ReduxUpdatedState,
    fetchAllPolicies: (sortBy: SortBy) => void,
    allPolicies: CoverNote[],
    fetchPolicyCount: (insurerId: InsurerId) => void,
    policyCount: ReduxUpdatedState,
    fetchAllArchived: (sortBy: SortBy) => void,
    allArchived: CoverNote[],
    fetchArchivedCount: (insurerId: InsurerId) => void,
    archivedCount: ReduxUpdatedState
}

interface RecordsState {
    coverNoteCount: string,
    policyCount: string,
    archivedCount: string,
    list: CoverNote[],
    sortBy: string,
    asc: boolean,
    page: number,
    loading: boolean,
    connected: boolean
}

class Records extends React.Component <RouteComponentProps & RecordsProps, RecordsState> {
    public componentMountedAt : moment.Moment = moment();
    recordsTable : HTMLElement;

    public constructor(props: RecordsProps) {
        super(props);
        this.state = {
            coverNoteCount: '-',
            policyCount: '-',
            archivedCount: '-',
            list: [],
            sortBy: RECORDS_SORT_BY.LAST_MODIFIED,
            asc: false,
            page: 1,
            loading: true,
            connected: true
        };
        this.trackBottomReach = this.trackBottomReach.bind(this);
        this.onError = this.onError.bind(this);
    }

    public componentDidMount() {
        this.props.fetchCoverNoteCount(this.props.insurerId());
        this.props.fetchPolicyCount(this.props.insurerId());
        this.props.fetchArchivedCount(this.props.insurerId());
        this.getContent(this.getOrder(this.state.sortBy, this.state.asc, 1));
        this.recordsTable = document.getElementById('recordsTable');
        document.addEventListener('scroll', this.trackBottomReach);
    }

    public componentWillUnmount() {
        document.removeEventListener('scroll', this.trackBottomReach);
    }

    public componentWillReceiveProps(nextProps: RecordsProps) {
        switch (this.props.content) {
            case CONTENT_TYPE.COVER_NOTE:
                if (nextProps.allCoverNotes !== this.props.allCoverNotes) this.setList(nextProps.allCoverNotes);
                break;
            case CONTENT_TYPE.POLICY:
                if (nextProps.allPolicies !== this.props.allPolicies) this.setList(nextProps.allPolicies);
                break;
            case CONTENT_TYPE.ARCHIVED:
                if (nextProps.allArchived !== this.props.allArchived) this.setList(nextProps.allArchived);
                break;
            default:
                break;
        }
        if (nextProps.newRecord !== this.props.newRecord) this.updateList(nextProps.newRecord);
        if (nextProps.newCount !== this.props.newCount) this.updateCount(nextProps.newCount);
        if (nextProps.coverNoteCount && this.props.coverNoteCount && nextProps.coverNoteCount.updatedAt.isAfter(this.componentMountedAt) && (nextProps.coverNoteCount.value !== this.props.coverNoteCount.value || this.state.coverNoteCount === '-')) {
            this.setState({
                coverNoteCount: nextProps.coverNoteCount.value.toString()
            });
        }
        if (nextProps.policyCount && this.props.policyCount && nextProps.policyCount.updatedAt.isAfter(this.componentMountedAt) && (nextProps.policyCount.value !== this.props.policyCount.value || this.state.policyCount === '-')) {
            this.setState({
                policyCount: nextProps.policyCount.value.toString()
            });
        }
        if (nextProps.archivedCount && this.props.archivedCount && nextProps.archivedCount.updatedAt.isAfter(this.componentMountedAt) && (nextProps.archivedCount.value !== this.props.archivedCount.value || this.state.archivedCount === '-')) {
            this.setState({
                archivedCount: nextProps.archivedCount.value.toString()
            });
        }
    }

    public setList(data: CoverNote[]) {
        this.setLoading(false);
        this.setState({
            list: (this.state.page === 1 ? [] : this.state.list).concat(data)
        });
    }

    public updateList(newRecord: CoverNote) {
        if (this.state.sortBy === RECORDS_SORT_BY.LAST_MODIFIED) {
            const data = newRecord.state.data;
            const path = this.props.history.location.pathname.split('/')[2];
            const isSameType = util.isSameContentType(path, data.props.policyType, data.props.policyEndDate, data.status);
            switch (data.status) {
                case FLOW_STATUS.CREATED:
                case FLOW_STATUS.CONFIRMED:
                case FLOW_STATUS.VOIDED:
                    this.updateRecord(isSameType, data.lastModifiedDate, data.initialRequestDate, data.midasRef, newRecord);
                    break;
                case FLOW_STATUS.INSURED:
                    if (path === SUB_DIR.POLICY) this.updateRecord(isSameType, data.lastModifiedDate, data.initialRequestDate, data.midasRef, newRecord);
                    else this.removeRecord(data.midasRef);
                    break;
                default:
                    break;
            }
        }
    }

    public updateRecord(isSameType: boolean, lastModifiedDate: string, initialRequestDate: string, refNo: string, newRecord) {
        if (isSameType) {
            if (lastModifiedDate !== initialRequestDate) this.removeRecord(refNo);
            this.state.list.unshift(newRecord);
        }
    }

    public removeRecord(refNo: string) {
        for (let i = 0; i < this.state.list.length; i++) {
            if (this.state.list[i].state.data.midasRef === refNo) {
                this.state.list.splice(i, 1);
            }
        }
    }

    public updateCount(data: RecordCount) {
        let coverNoteCount = this.state.coverNoteCount;
        if (this.state.coverNoteCount !== '-') coverNoteCount = (parseInt(coverNoteCount, 10) + data.coverNote).toString();
        let policyCount = this.state.policyCount;
        if (this.state.policyCount !== '-') policyCount = (parseInt(policyCount, 10) + data.policy).toString();
        let archivedCount = this.state.archivedCount;
        if (this.state.archivedCount !== '-') archivedCount = (parseInt(archivedCount, 10) + data.archived).toString();
        this.setState({
            coverNoteCount,
            policyCount,
            archivedCount
        });
    }

    public setLoading(loading: boolean) {
        this.setState({loading});
    }

    public getContent(order: SortBy) {
        switch (this.props.content) {
            case CONTENT_TYPE.COVER_NOTE:
                this.props.fetchAllCoverNotes(order);
                break;
            case CONTENT_TYPE.POLICY:
                this.props.fetchAllPolicies(order);
                break;
            case CONTENT_TYPE.ARCHIVED:
                this.props.fetchAllArchived(order);
                break;
            default:
                break;
        }
    }

    public getOrder(sortBy: string, asc: boolean, page: number) {
        return {
            ...this.props.insurerId(), ...{
                page,
                sortCriteria: sortBy === RECORDS_SORT_BY.DAY_LEFT ? RECORDS_SORT_BY.END_DATE : sortBy,
                order: asc ? ORDER_BY.ASC : ORDER_BY.DESC
            }
        };
    }

    public clickSort(sortBy: string) {
        const asc = this.state.sortBy === sortBy ? !this.state.asc : true;
        this.setLoading(true);
        this.setState(
          {
            list: [],
            sortBy,
            asc,
            page: 1,
          },
          () => this.getContent(this.getOrder(this.state.sortBy, asc, 1))
        );
    }

    public clickButton(dest: string) {
        this.props.history.push(dest);
    }

    public trackBottomReach() {
        if(this.state.loading) return;
        let count = 0;
        switch (this.props.content) {
            case CONTENT_TYPE.COVER_NOTE:
                count = +this.state.coverNoteCount;
                break;
            case CONTENT_TYPE.POLICY:
                count = +this.state.policyCount;
                break;
            case CONTENT_TYPE.ARCHIVED:
                count = +this.state.archivedCount;
                break;
            default:
                break;
        }
        if (this.state.list.length < count && this.recordsTable.getBoundingClientRect().bottom <= window.innerHeight) {
            const previousPage = this.state.page;
            this.setLoading(true);
            this.setState({page: previousPage + 1});
            this.getContent(this.getOrder(this.state.sortBy, this.state.asc, previousPage + 1));
        }
    }

    public onError() {
        this.setLoading(false);
        this.setState({
            connected: false
        });
    }

    public render() {
        let loader;
        let table;
        if (this.state.loading && this.state.connected) { loader = <img className="cmHorCenter recordsPageSpinner" src={pageSpinner}/>; }
        if (!this.state.loading && this.state.connected && !this.state.list.length) { table = <NoRecords page={this.props.content}/>; }
        if (this.state.list.length > 0) {
            table = <RecordsTableView
                        page={this.props.content}
                        list={this.state.list}
                        sortBy={this.state.sortBy}
                        clickSort={(sortBy) => this.clickSort(sortBy)}/>
        }
        return (
            <div id="recordsTable" className="cmHorCenter records">
                <div className="recordsNotification"><MaintenanceNotification isClearable={true}/></div>
                <div className="recordsButtonsContainer">
                    <RecordsBannerButton
                        buttonType={CONTENT_TYPE.COVER_NOTE}
                        count={this.state.coverNoteCount}
                        isSelected={this.props.content === CONTENT_TYPE.COVER_NOTE}
                        onClick={() => this.clickButton(URL.COVER_NOTE)}/>
                    <RecordsBannerButton
                        buttonType={CONTENT_TYPE.POLICY}
                        count={this.state.policyCount}
                        isSelected={this.props.content === CONTENT_TYPE.POLICY}
                        onClick={() => this.clickButton(URL.POLICY)}/>
                    <RecordsBannerButton
                        buttonType={CONTENT_TYPE.ARCHIVED}
                        count={this.state.archivedCount}
                        isSelected={this.props.content === CONTENT_TYPE.ARCHIVED}
                        onClick={() => this.clickButton(URL.ARCHIVED)}/>
                    <RecordsSquareButton buttonType={FORM_ACTION_TYPE.CREATE}/>
                    <RecordsSquareButton buttonType={FORM_ACTION_TYPE.BATCH}/>
                </div>
                <ErrorHandler onError={this.onError} displayAtCenter={true}/>
                {table}
                {loader}
            </div>
        );
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Records));



// WEBPACK FOOTER //
// ./src/Components/Records/Records.tsx